import type { ComboboxItemWithValue } from "@replo/design-system/components/combobox/Combobox";
import type { MoveProjectModalProps } from "@v2/types/modalTypes";

import * as React from "react";

import { LabeledControl } from "@replo/design-system/blocks/labeled-control/LabeledControl";
import { Button } from "@replo/design-system/components/button/Button";
import {
  ComboboxContent,
  ComboboxEmpty,
  ComboboxInput,
  ComboboxItem,
  ComboboxList,
  ComboboxRoot,
} from "@replo/design-system/components/combobox/Combobox";
import { Drawer } from "@replo/design-system/components/drawer/Drawer";
import { Modal } from "@replo/design-system/components/modal/Modal";
import { toast } from "@replo/design-system/components/toast/Toast";
import { useIsMobile } from "@replo/design-system/hooks/use-mobile";
import { elementUtils } from "@v2/trpc/routes/element";
import { projectRouter, projectUtils } from "@v2/trpc/routes/project";
import { workspaceRouter, workspaceUtils } from "@v2/trpc/routes/workspace";
import { docs } from "@v2/utils/docs";
import { getProjectLabels } from "@v2/utils/project-labels";
import { ChevronDownIcon } from "lucide-react";
import { Controller, useForm, useWatch } from "react-hook-form";
import { generatePath, useLocation, useNavigate } from "react-router-dom";
import { isEmpty } from "replo-utils/lib/misc";

type FormValues = {
  newWorkspaceId: string | null;
};

const WorkspaceCombobox: React.FC<{
  items: ComboboxItemWithValue[];
  selectedItem: ComboboxItemWithValue | undefined;
  onValueChange: (item: ComboboxItemWithValue | null) => void;
}> = ({ items, selectedItem, onValueChange }) => {
  const anchorRef = React.useRef<HTMLDivElement>(null);

  return (
    <ComboboxRoot
      items={items}
      value={selectedItem ?? null}
      onValueChange={onValueChange}
      itemToStringValue={(item: ComboboxItemWithValue) => String(item.value)}
      isItemEqualToValue={(a, b) => a.value === b.value}
    >
      <ComboboxInput
        placeholder="Select a workspace"
        wrapperRef={anchorRef}
        endEnhancer={<ChevronDownIcon />}
      />
      <ComboboxContent anchor={anchorRef}>
        <ComboboxEmpty />
        <ComboboxList>
          {(item: ComboboxItemWithValue) => (
            <ComboboxItem key={item.value} {...item} value={item} />
          )}
        </ComboboxList>
      </ComboboxContent>
    </ComboboxRoot>
  );
};

export const MoveProjectModal: React.FC<
  MoveProjectModalProps & {
    open: boolean;
    onOpenChange: (open: boolean) => void;
  }
> = ({
  projectId,
  oldWorkspaceId,
  shopifyUrl,
  hostingType,
  open,
  onOpenChange,
}) => {
  const isMobile = useIsMobile();
  const { mutate: transferProject, isPending: isLoading } =
    projectRouter.transfer.useMutation({
      onSuccess: ({ newWorkspaceId }) => {
        void workspaceUtils.getUserWorkspacesList.invalidate();
        void projectUtils.findByUserId.invalidate();
        void projectUtils.listWithStats.invalidate();
        toast({ title: "Project transferred" });
        if (pathname.includes("/workspace")) {
          navigate(
            generatePath("/workspace/:workspaceId/projects", {
              workspaceId: newWorkspaceId,
            }),
          );
        }
        if (pathname.includes("/shops")) {
          void elementUtils.getById.invalidate();
          void elementUtils.listAllElementsMetadata.invalidate();
          void projectUtils.getV2.invalidate();
          void projectUtils.listGroupedByWorkspaceForUser.invalidate();
        }
      },
      onSettled: () => onOpenChange(false),
    });

  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { data } = workspaceRouter.getUserWorkspacesList.useQuery();

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<FormValues>({
    mode: "onChange",
    defaultValues: {
      newWorkspaceId: null,
    },
  });
  const newWorkspaceIdValue = useWatch({ control, name: "newWorkspaceId" });

  if (!oldWorkspaceId) {
    return null;
  }

  const filteredWorkspaces = data?.workspaces.filter((workspace) => {
    return (
      workspace.id !== oldWorkspaceId &&
      workspace.workspaceMemberships[0]?.role === "owner"
    );
  });

  const hasWorkspaceIdErrors = !isEmpty(errors.newWorkspaceId);

  const onSubmit = ({ newWorkspaceId }: FormValues) => {
    if (newWorkspaceId && oldWorkspaceId) {
      transferProject({
        projectId,
        newWorkspaceId,
      });
    }
  };

  const labels = getProjectLabels(hostingType);

  const title = `Move ${labels.singularCapitalized}`;

  const footer = (
    <div className="flex justify-end gap-2 w-full">
      <Button
        variant="secondary"
        size="base"
        onClick={() => onOpenChange(false)}
      >
        Cancel
      </Button>
      <Button
        variant="primary"
        size="base"
        render={<button type="submit" form="move-project-form" />}
        disabled={hasWorkspaceIdErrors || !newWorkspaceIdValue}
        loading={isLoading}
      >
        Confirm
      </Button>
    </div>
  );

  const formContent = (
    <form
      id="move-project-form"
      className="flex flex-col gap-6"
      onSubmit={(data) => {
        void handleSubmit(onSubmit)(data);
      }}
    >
      <LabeledControl
        label="Which workspace do you want to move this to?"
        description={
          shopifyUrl && (
            <span>
              Please note: The Shopify store currently connected to this project
              will be moved to the new workspace. If this Shopify store is used
              for billing, or the new workspace has another Shopify store which
              is used for billing, you will not be able to move the project.{" "}
              <a
                className="text-primary underline"
                href={
                  docs.errors
                    .projectConnectedToBillingIntegrationForShopifyWorkspace
                }
              >
                Learn More
              </a>
            </span>
          )
        }
      >
        <Controller
          name="newWorkspaceId"
          control={control}
          render={({ field: { onChange, value } }) => {
            const items: ComboboxItemWithValue[] =
              filteredWorkspaces?.map((workspace) => ({
                label: workspace.name,
                value: workspace.id,
              })) ?? [];
            const selectedItem = items.find((item) => item.value === value);
            return (
              <WorkspaceCombobox
                items={items}
                selectedItem={selectedItem}
                onValueChange={(item) => {
                  onChange(item?.value ?? null);
                }}
              />
            );
          }}
        />
      </LabeledControl>
    </form>
  );

  if (isMobile) {
    return (
      <Drawer
        open={open}
        onOpenChange={onOpenChange}
        title={title}
        footer={footer}
      >
        {formContent}
      </Drawer>
    );
  }

  return (
    <Modal
      title={title}
      open={open}
      onOpenChange={onOpenChange}
      size="base"
      footer={footer}
    >
      {formContent}
    </Modal>
  );
};
